import apiClient from "../../../helpers/apiClient";

/**
 * Decode VIN via backend — proxies NHTSA / other providers and avoids browser CORS.
 * Expects POST /api/car/decode-vin with JSON body `{ vin: string }`.
 *
 * Accepts typical shapes: `{ success, data: { make, model, year, ... } }` or a flat object.
 *
 * @param {string} vin
 * @returns {Promise<{ vin: string, make: string, model: string, year: number, fueltype1: string, trany: string, vclass: string }>}
 */

export default async function decodeVinFromNhtsa(vin) {
    const clean = vin.trim().toUpperCase();

    let data;
    try {
        const response = await apiClient.post("/api/car/decode-vin", { vin: clean });
        data = response.data;
    } catch (error) {
        const msg = error?.response?.data?.message || error?.message || "decode failed";
        throw new Error(msg);
    }

    if (data && data.success === false) {
        throw new Error(data.message || "decode failed");
    }

    const raw = data?.data ?? data;
    if (!raw || typeof raw !== "object") {
        throw new Error("invalid response");
    }

    const make = String(raw.make ?? raw.Make ?? "").trim();
    const model = String(raw.model ?? raw.Model ?? "").trim();
    const year = parseInt(String(raw.year ?? raw.ModelYear ?? ""), 10);

    const fueltype1 = String(
        raw.fueltype1 ?? raw.fuelType ?? raw.FuelTypePrimary ?? raw.FuelTypeSecondary ?? "",
    ).trim();
    const trany = String(raw.trany ?? raw.transmission ?? raw.TransmissionStyle ?? "").trim();
    const vclass = String(
        raw.vclass ?? raw.bodyClass ?? raw.bodyStyle ?? raw.BodyClass ?? raw.VehicleType ?? "",
    ).trim();

    if (!make || !model || Number.isNaN(year)) {
        throw new Error("incomplete decode");
    }

    return {
        vin: clean,
        make,
        model,
        year,
        fueltype1,
        trany,
        vclass,
    };
}
